import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Plus } from "lucide-react";

import { HealthlinkCredentialsModal } from "components/HealthlinkCredentialsModal/HealthlinkCredentialsModal.jsx";
import { Loader } from "components/Loader/Loader";
import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { Button } from "components/ui";
import { HseDetailsNotice } from "components/ui/HseDetailsNotice/HseDetailsNotice";
import { Pill } from "components/ui/Pill/Pill";
import { useConsultationsList } from "features/consultations/hooks/useConsultationsList";
import { usePatientFamilyMedicalHistory } from "features/family/hooks/usePatientFamilyMedicalHistory";
import { HealthlinkSubmissionStatus } from "features/healthlink/components/HealthlinkSubmissionStatus/HealthlinkSubmissionStatus";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { getHealthlinkStatusColors } from "features/healthlink/utils/healthlinkStatusTone";
import { canPatientReachHse } from "features/healthlink/utils/hsePatientReadiness";
import { usePatientLongTermMedications } from "features/medications/hooks/usePatientLongTermMedications";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useAllergies } from "features/patients/hooks/useAllergies";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useMedicalHistory } from "features/patients/hooks/useMedicalHistory";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { usePatientPreferences } from "features/patients/hooks/usePatientPreferences";
import { usePatientSocial } from "features/patients/hooks/usePatientSocial";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";

import { AddReferralForm } from "./components/AddReferralForm/AddReferralForm.jsx";
import { AdditionalInformationsForm } from "./components/AddReferralForm/components/AdditionalInformationsForm/AdditionalInformationsForm.jsx";
import { getPrefillErrorLabels } from "./components/AddReferralForm/utils/getPrefillErrorLabels";
import { ReferralPreview } from "./components/ReferralPreview/ReferralPreview.jsx";
import { ResultsTable } from "./components/ResultsTable/ResultsTable.jsx";
import { XMLReferralDataLayerWrapper } from "./components/ResultsTable/XMLReferralDataLayerWrapper.jsx";
import { useIbReferralReturn } from "./hooks/useIbReferralReturn";
import { useReferralActions } from "./hooks/useReferralActions";
import { useView, views } from "./hooks/useView.js";
import s from "./PatientReferralsPage.module.css";
import { fetchPatientById } from "./utils/fetchPatientById.js";
import {
  getDisplayStatus,
  getReferralStatusKey,
} from "./utils/referralStatus";

const PatientReferralsPage = () => {
  const [view, setView] = useView();
  const { data: staffInfo } = useStaffInfo();
  const [selectedReferral, setSelectedReferral] = useState(null);

  const { data: healthlinkPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials } = useHealthlinkCredentials();

  const queryClient = useQueryClient();
  const patientId = useCurrentPatientId();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  // Confirm-on-return from the Healthlink Integrated Browser; keeps the list in
  // a loading state until the returned referral is processed.
  const { isProcessingReturn } = useIbReferralReturn();

  // Selected-referral actions: refresh, resend / regenerate, manual check.
  const {
    handleRefresh,
    handleResendClick,
    handleCheckStatus,
    isResending,
    isCheckingStatus,
  } = useReferralActions({ selectedReferral, setSelectedReferral });

  const hasGpDetailsSaves =
    !!healthlinkPractice &&
    Array.isArray(healthlinkCredentials) &&
    healthlinkCredentials.length > 0;

  const { data: patient } = useQuery({
    queryKey: ["patient", patientId, tenantVal],
    queryFn: () => fetchPatientById(patientId, tenantVal),
  });

  // A referral carries the patient's date of birth and gender to the hospital
  // (PatientIdentification), so it cannot be sent without them (KIN-1733).
  const isBlockedByPatientDetails =
    !!patient && !canPatientReachHse(patient);

  const { data: patientBaseline, isError: isPatientBaselineError } =
    usePatientBaseline(patientId);

  const { data: patientPreferences } = usePatientPreferences(patientId);
  const { data: patientSocial, isError: isPatientSocialError } =
    usePatientSocial(patientId);
  const { data: medicalHistory, isError: isMedicalHistoryError } =
    useMedicalHistory(patient?.id);
  const { data: familyMedicalHistory, isError: isFamilyMedicalHistoryError } =
    usePatientFamilyMedicalHistory(patient?.id);
  const { data: allergies, isError: isAllergiesError } = useAllergies(
    patient?.id
  );
  const { data: longTermMedications, isError: isLongTermMedicationsError } =
    usePatientLongTermMedications(patient?.id);
  const { data: consultationsList } = useConsultationsList(patient?.id);

  const prefillErrors = getPrefillErrorLabels({
    allergies: isAllergiesError,
    medicalHistory: isMedicalHistoryError,
    familyMedicalHistory: isFamilyMedicalHistoryError,
    longTermMedications: isLongTermMedicationsError,
    patientSocial: isPatientSocialError,
    patientBaseline: isPatientBaselineError,
  });

  // The badge's own tones, from this page's existing status keys.
  const statusTone = {
    completed: "positive",
    accepted: "positive",
    external: "pending",
    draft: "info",
    rejected: "negative",
    undelivered: "negative",
  }[getReferralStatusKey(selectedReferral?.status)];

  return (
    <SimplePatientPageLayout>
      <div className={s.secondColumnSubWrapper}>
        {/* Above the cards rather than inside one, matching where eLab puts the same
            notice — and shown on the list and in the wizard alike, so a GP learns it
            before starting rather than at step four (KIN-1733). */}
        <HseDetailsNotice
          patient={patient}
          patientId={patientId}
          submissionName="as a referral"
        />
        {(view === views.general ||
          (!selectedReferral && view === views.refferalOverview)) && (
          <div className={s.mainWrapper}>
            <div className={s.generalInfoTitleWrapper}>
              <h1 className={s.generalInfoTitle}>Referrals</h1>
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  alignItems: "center",
                }}
              >
                <ProcessMessagesButton
                  title="Process referral responses"
                  successMessage="Referral responses processed successfully!"
                />
                {!hasGpDetailsSaves ? (
                  <HealthlinkCredentialsModal style={{ marginTop: "0px" }} />
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setView(views.addNewReferral);
                    }}
                    endIcon={Plus}
                    disabled={isBlockedByPatientDetails}
                    title={
                      isBlockedByPatientDetails
                        ? "This patient's details are incomplete — a referral cannot be sent for them"
                        : undefined
                    }
                  >
                    Add referral
                  </Button>
                )}
              </div>
            </div>
            {isProcessingReturn ? (
              <Loader>Confirming your Healthlink referral…</Loader>
            ) : (
              <ResultsTable
                patientId={patientId}
                tenantVal={tenantVal}
                setSelectedReferral={(item) => {
                  setSelectedReferral(item);
                  setView(views.refferalOverview);
                }}
              />
            )}
          </div>
        )}
        {view === views.addNewReferral && (
          <div className={s.mainWrapper} style={{ padding: "0" }}>
            <div
              className={s.generalInfoTitleWrapper}
              style={{
                padding: "24px",
                paddingBottom: "0",
                marginBottom: "0",
              }}
            >
              <div className={s.backButtonWrapper}>
                <Button
                  variant="outline"
                  size="small"
                  className={s.backButton}
                  aria-label="Back"
                  onClick={() => {
                    setView(views.general);
                  }}
                >
                  <ArrowLeft />
                </Button>
                <h1 className={s.generalInfoTitle}>
                  Referral{" "}
                  <span className={s.referralDate}>
                    {format(new Date(), "dd-MM-yyyy")}
                  </span>
                </h1>
              </div>
            </div>
            <AddReferralForm
              patientId={patientId}
              patient={patient}
              staffMember={staffInfo?.staffMember}
              wrapperStyle={{
                width: "100%",
              }}
              onSuccess={() => {
                setView(views.general);
              }}
              patientBaseline={patientBaseline}
              patientPreferences={patientPreferences}
              patientSocial={patientSocial}
              medicalHistory={medicalHistory}
              familyMedicalHistory={familyMedicalHistory}
              allergies={allergies}
              longTermMedications={longTermMedications}
              consultationsList={consultationsList}
              prefillErrors={prefillErrors}
            />
          </div>
        )}
        {view === views.refferalOverview && selectedReferral && (
          <div className={s.mainWrapper} style={{ padding: "0" }}>
            <div
              className={s.generalInfoTitleWrapper}
              style={{
                padding: "24px",
                paddingBottom: "0",
                marginBottom: "0",
              }}
            >
              <div className={s.backButtonWrapper}>
                <Button
                  variant="outline"
                  size="small"
                  className={s.backButton}
                  aria-label="Back"
                  onClick={() => {
                    setView(views.general);
                    setSelectedReferral(null);
                  }}
                >
                  <ArrowLeft />
                </Button>
                <h1 className={s.generalInfoTitle}>
                  Referral{" "}
                  <span className={s.referralDate}>
                    {format(new Date(), "dd-MM-yyyy")}
                  </span>
                </h1>
              </div>
              <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                {["ERROR", "DRAFT", "INTEGRATED_BROWSER"].includes(
                  selectedReferral.status
                ) && (
                  <Button
                    variant="text"
                    onClick={handleResendClick}
                    disabled={isResending}
                  >
                    {isResending
                      ? "Working..."
                      : ["DRAFT", "INTEGRATED_BROWSER"].includes(
                          selectedReferral.status
                        )
                      ? "Regenerate link"
                      : "Resend"}
                  </Button>
                )}
                <Pill {...getHealthlinkStatusColors(statusTone)}>
                  {getDisplayStatus(selectedReferral.status)}
                </Pill>
                {["DRAFT", "INTEGRATED_BROWSER"].includes(
                  selectedReferral.status
                ) && (
                  <Button
                    variant="primary"
                    onClick={handleCheckStatus}
                    disabled={isCheckingStatus}
                  >
                    {isCheckingStatus ? "Checking..." : "Check Status"}
                  </Button>
                )}
              </div>
            </div>
            <div className={s.referralPreviewWrapper}>
              {!!selectedReferral?.xmlResponseData &&
                !selectedReferral.xmlResponseData.startsWith("<?xml") &&
                ["DRAFT", "INTEGRATED_BROWSER"].includes(
                  selectedReferral.status
                ) && (
                  <AdditionalInformationsForm
                    link={selectedReferral.xmlResponseData}
                    referralId={selectedReferral.id}
                    hideFinishButton={false}
                    onContinue={() => {
                      setSelectedReferral((prev) => ({
                        ...prev,
                        status: "SENT",
                      }));
                      toast.success(
                        "Referral submitted to Healthlink successfully!"
                      );
                      queryClient.invalidateQueries({
                        queryKey: ["referralsList"],
                      });
                    }}
                  />
                )}
              <ReferralPreview
                data={selectedReferral}
                onRefresh={handleRefresh}
                isLungReferral={selectedReferral?.referralType?.includes(
                  "lung"
                )}
                isProstateReferral={selectedReferral?.referralType?.includes(
                  "prostate"
                )}
                hideData={true}
              />
              <XMLReferralDataLayerWrapper
                messageControlId={selectedReferral.messageControlId}
              />
              {/* Where this referral stands with Healthlink — the same section the eLab
                  order detail ends on, so the two Healthlink flows answer the question
                  the same way. The pill is this page's own status styling. */}
              <HealthlinkSubmissionStatus
                statusPill={
                  <Pill {...getHealthlinkStatusColors(statusTone)}>
                    {getDisplayStatus(selectedReferral.status)}
                  </Pill>
                }
                submitted={selectedReferral.date}
                messageId={selectedReferral.messageControlId}
              />
            </div>
          </div>
        )}
      </div>
    </SimplePatientPageLayout>
  );
};

export { PatientReferralsPage };
